
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Settings() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [soundEffects, setSoundEffects] = useState(true);
  const [autoConnect, setAutoConnect] = useState(false);
  const [language, setLanguage] = useState('العربية');

  const [settingsGroups] = useState([
    {
      title: 'الإعدادات العامة',
      items: [
        {
          icon: 'ri-moon-line',
          title: 'الوضع المظلم',
          subtitle: 'تفعيل الثيم المظلم',
          type: 'toggle',
          value: darkMode,
          onChange: setDarkMode
        },
        {
          icon: 'ri-global-line',
          title: 'اللغة',
          subtitle: language,
          type: 'select',
          value: language,
          onChange: setLanguage,
          options: ['العربية', 'English', 'Français']
        }
      ]
    },
    {
      title: 'الإشعارات',
      items: [
        {
          icon: 'ri-notification-3-line',
          title: 'الإشعارات',
          subtitle: 'تلقي الإشعارات',
          type: 'toggle',
          value: notifications,
          onChange: setNotifications
        },
        {
          icon: 'ri-volume-up-line',
          title: 'الأصوات',
          subtitle: 'تشغيل الأصوات',
          type: 'toggle',
          value: soundEffects,
          onChange: setSoundEffects
        }
      ]
    },
    {
      title: 'الاتصال',
      items: [
        {
          icon: 'ri-wifi-line',
          title: 'الاتصال التلقائي',
          subtitle: 'الاتصال التلقائي بالركاب',
          type: 'toggle',
          value: autoConnect,
          onChange: setAutoConnect
        }
      ]
    },
    {
      title: 'المعلومات',
      items: [
        {
          icon: 'ri-shield-check-line',
          title: 'الخصوصية',
          subtitle: 'سياسة الخصوصية',
          type: 'navigate',
          action: () => navigate('/privacy')
        },
        {
          icon: 'ri-file-text-line',
          title: 'شروط الاستخدام',
          subtitle: 'الشروط والأحكام',
          type: 'navigate',
          action: () => navigate('/terms')
        },
        {
          icon: 'ri-information-line',
          title: 'حول التطبيق',
          subtitle: 'الإصدار 1.2.0',
          type: 'navigate',
          action: () => navigate('/about')
        }
      ]
    }
  ]);

  const renderSettingItem = (item: any) => {
    if (item.type === 'toggle') {
      return (
        <div className="flex items-center">
          <button
            onClick={() => item.onChange(!item.value)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              item.value ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                item.value ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      );
    }

    if (item.type === 'select') {
      return (
        <button 
          onClick={() => {/* Handle language selection */}}
          className="flex items-center text-blue-600"
        >
          <span className="text-sm font-arabic mr-2">{item.value}</span>
          <i className="ri-arrow-left-s-line"></i>
        </button>
      );
    }

    if (item.type === 'navigate') {
      return (
        <button onClick={item.action} className="text-gray-400">
          <i className="ri-arrow-left-s-line"></i>
        </button>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 pt-12 pb-4 px-6">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate('/main')}
            className="w-10 h-10 flex items-center justify-center"
          >
            <i className="ri-arrow-right-line text-xl text-gray-600"></i>
          </button>
          
          <h1 className="text-lg font-bold text-gray-800 font-arabic">الإعدادات</h1>
          
          <div className="w-10 h-10"></div>
        </div>
      </div>

      {/* Settings Groups */}
      <div className="px-6 py-4">
        {settingsGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="mb-6">
            <h2 className="text-sm font-bold text-gray-600 mb-3 px-2 font-arabic">
              {group.title}
            </h2>
            
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {group.items.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  className="flex items-center p-4 border-b border-gray-100 last:border-b-0"
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center ml-4">
                    <i className={`${item.icon} text-gray-600`}></i>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-800 font-arabic">{item.title}</h3>
                    <p className="text-sm text-gray-500 font-arabic">{item.subtitle}</p>
                  </div>
                  
                  {renderSettingItem(item)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Support Section */}
      <div className="px-6 mb-20">
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-6 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-customer-service-2-line text-2xl"></i>
          </div>
          <h3 className="text-lg font-bold mb-2 font-arabic">تحتاج مساعدة؟</h3>
          <p className="text-white/80 text-sm mb-4 font-arabic">
            فريق الدعم متاح 24/7 لمساعدتك
          </p>
          <button 
            onClick={() => navigate('/support')}
            className="bg-white text-blue-600 px-6 py-2 rounded-full font-medium font-arabic hover:bg-white/90 transition-colors"
          >
            اتصل بالدعم
          </button>
        </div>
      </div>

      <style jsx>{`
        .font-arabic {
          font-family: 'Tajawal', 'Noto Sans Arabic', sans-serif;
        }
      `}</style>
    </div>
  );
}